import { useCallback, useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { getLoginLink } from '@confluence/login-if-anonymous/entry-points/getLoginLink.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { cfetch } from '@confluence/network';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

export type ConfluenceJoinabilityType = 'canJoinDirectly' | 'licensingInProgress' | null;

type IsAuthenticatedUnlicensedDirectAccessType = {
	confluenceJoinability: ConfluenceJoinabilityType;
	identityLink: string;
	isDomainAccessLoading: boolean;
};

// See here https://hello.atlassian.net/wiki/spaces/ETRUST/pages/640596761/API+Guide+-+v1+access-requests+request#Enum-Definitions
// for the enum definitions
enum AuthorizedCapabilities {
	DIRECT_ACCESS = 'DIRECT_ACCESS',
	ACCESS_EXISTS = 'ACCESS_EXISTS',
	ACCESS_GRANTED = 'ACCESS_GRANTED',
	APPROVED_REQUEST_EXISTS = 'APPROVED_REQUEST_EXISTS',
}

// These would be the bucket of enums that we are looking for which would convey that a user's newly
// granted license hasn't synced with Confluence yet
const awaitingLicenseSyncBucket = [
	AuthorizedCapabilities.ACCESS_EXISTS,
	AuthorizedCapabilities.ACCESS_GRANTED,
	AuthorizedCapabilities.APPROVED_REQUEST_EXISTS,
];

const getJoinability = (
	capability: AuthorizedCapabilities | null,
	isAuthenticatedAndUnlicensed: boolean,
): ConfluenceJoinabilityType => {
	if (!isAuthenticatedAndUnlicensed || capability === null) {
		return null;
	}
	if (capability === AuthorizedCapabilities.DIRECT_ACCESS) {
		return 'canJoinDirectly';
	}
	if (awaitingLicenseSyncBucket.includes(capability)) {
		return 'licensingInProgress';
	}
	return null;
};

/**
 * This hook is used to tell if the current user is logged in and is currently
 * authenticated through Confluence as an org member that is unlicensed or anonymous
 * depending on the site's settings, so they would lack full product access.
 *
 * The hook also checks if they can join Confluence directly (grant themselves a product license) via an Identity flow,
 * the link to which is returned in identityLink. (This same Identity access flow can be used to request a license
 * if the user can't join directly, but there is a bug on the Identity side that makes it not redirect properly for users with
 * this access status if they require an admin approval to join.)
 * Context here: https://hello.atlassian.net/wiki/spaces/~rpillay/pages/2393998255
 * Furthermore, this hook checks if the user has been granted a license but is waiting on the license to sync with Confluence so that
 * consumers can show an intermediate state to the user.
 *
 *
 * Sidenote: A user that has just created a Confluence account and has logged in will not
 * have a user ID.  To be an org member, they will need to have been previously authenticated through
 * Confluence licensing or by some other product means.
 */
export const useIsAuthenticatedUnlicensedWithDirectAccess = ({
	skipDomainCheck = false,
}: {
	skipDomainCheck?: boolean;
}): IsAuthenticatedUnlicensedDirectAccessType => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, userId, accessStatus } = useSessionData();
	const [approvedCapabiity, setApprovedCapability] = useState<AuthorizedCapabilities | null>(null);
	const [isDomainAccessLoading, setIsDomainAccessLoading] = useState(false);
	const [currentPageLink, setCurrentPageLink] = useState<string | null>(null);

	const isAuthenticatedAndUnlicensed =
		accessStatus === AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS ||
		accessStatus === AccessStatus.ANONYMOUS_ACCESS;

	const checkDirectAccessCapability = useCallback(async () => {
		const params = new URLSearchParams({
			resource: `ari:cloud:confluence::site/${cloudId}`,
		});
		try {
			const response = await cfetch(
				`/gateway/api/invitations/v1/access-requests/capabilities?${params.toString()}`,
				{
					method: 'GET',
					headers: {
						resource: `ari:cloud:confluence::site/${cloudId}`,
						ACCEPT: 'application/json',
						'Account-Id': userId || '',
					},
				},
			);
			const data = await response.json();
			const capability = Object.values(data.results)[0] as string;
			if ((Object.values(AuthorizedCapabilities) as string[]).includes(capability)) {
				if (capability === AuthorizedCapabilities.DIRECT_ACCESS) {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							source: 'restrictedSpaceScreen',
							actionSubject: 'approvedAuthenticatedUnlicensedUser',
							action: 'identified',
						},
					}).fire();
				}
				setApprovedCapability(capability as AuthorizedCapabilities);
			}
		} catch (e) {
			getMonitoringClient().submitError(e, {
				attribution: Attribution.PERMISSIONS_EXPERIENCE,
			});
		}
	}, [cloudId, userId, createAnalyticsEvent]);

	useEffect(() => {
		const checkDomainJoinCapability = async () => {
			setIsDomainAccessLoading(true);
			await checkDirectAccessCapability();
			setIsDomainAccessLoading(false);
		};
		if (isAuthenticatedAndUnlicensed && !skipDomainCheck) {
			void checkDomainJoinCapability();
		}
	}, [cloudId, userId, isAuthenticatedAndUnlicensed, checkDirectAccessCapability, skipDomainCheck]);

	useEffect(
		() => {
			// Window is not defined if this component is rendered in SSR
			if (typeof window !== 'undefined') {
				// Identity requires an absolute URL for redirection into Confluence
				// eslint-disable-next-line no-restricted-syntax
				setCurrentPageLink(encodeURIComponent(window.location.href));
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const identityLink = getLoginLink(
		(identity) =>
			`https://${identity}/join/user-access?resource=ari:cloud:confluence::site/${cloudId}&continue=${currentPageLink}`,
	);

	return {
		confluenceJoinability: getJoinability(approvedCapabiity, isAuthenticatedAndUnlicensed),
		identityLink,
		isDomainAccessLoading,
	};
};
